/* eslint-disable react-hooks/exhaustive-deps */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { GridActionsCellItem, GridColDef, GridRowId, GridFilterModel } from '@mui/x-data-grid';
import { Edit, Trash2 } from 'feather-icons-react';
import { useEffect, useState } from 'react';

import { useAdformDealsQuery } from '../../modules/apiData/dataApiSlice';
import { useAuth } from '../../modules/auth/useAuth';
import { Publisher } from '../../modules/publisher/publisherApiTypes';
import { usePublisher } from '../../modules/publisher/usePublisher';
import { UserRoles } from '../../modules/user/userApiTypes';
import { Button, Grid, Modal, Tooltip, Spin } from '../common';
import { CreatePublisherModal } from './CreatePublisherModal';
import { DeletePublisherModal } from './DeletePublisherModal';
import { EditPublisherModal } from './EditPublisherModal';
import { UploadFileModal } from './UploadFileModal';

export const PublishersSettings = () => {
  const { role } = useAuth();
  const canEdit = role === UserRoles.SUPER_ADMIN.value || role === UserRoles.INTERNAL_ADMIN.value;
  const { publishers, getPublishers, isLoading, refreshFlag } = usePublisher();
  const { data: adformDeals } = useAdformDealsQuery();
  const [selectedPublisher, setSelectedPublisher] = useState<undefined | Publisher>(undefined);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isUploadFileModalOpen, setIsUploadFileModalOpen] = useState(false);
  const [createModalKey, setCreateModalKey] = useState(0);
  const [editModalKey, setEditModalKey] = useState(0);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  const openCreateModal = () => {
    setCreateModalKey((prev) => prev + 1);
    setIsCreateModalOpen(true);
  };
  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const openEditModal = () => {
    setEditModalKey((prev) => prev + 1);
    setIsEditModalOpen(true);
  };
  const closeEditModal = () => {
    setIsEditModalOpen(false);
  };

  const openDeleteModal = () => setIsDeleteModalOpen(true);
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const openUploadFileModal = () => setIsUploadFileModalOpen(true);
  const closeUploadFileModal = () => {
    setIsUploadFileModalOpen(false);
  };

  useEffect(() => {
    getPublishers();
  }, []);

  useEffect(() => {
    if (!refreshFlag) getPublishers();
  }, [refreshFlag]);

  const handleEditClick = (id: GridRowId) => () => {
    const publisher = publishers?.find((el: { id: GridRowId }) => el.id === id);

    setSelectedPublisher(publisher);
    openEditModal();

    return id;
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    const publisher = publishers?.find((el: { id: GridRowId }) => el.id === id);

    setSelectedPublisher(publisher);
    openDeleteModal();

    return id;
  };

  // Prepare data with computed fields for filtering
  const enrichedPublishers = publishers?.map((publisher) => ({
    ...publisher,
    dealsString:
      publisher.dealIds
        ?.map((id: string) => adformDeals?.find((deal) => deal.dealId === id)?.name)
        .filter(Boolean)
        .join(', ') || '',
    sitesString: publisher.siteDomains?.join(', ') || '',
    appsString: publisher.appIds?.join(', ') || '',
  }));

  const columns: GridColDef[] = [
    {
      field: 'displayName',
      headerName: 'Publishers',
      flex: 1,
      headerClassName: 'member-cell',
      minWidth: 100,
      filterable: true,
      renderCell: (params) => {
        const publisher = params.row;

        return (
          <div className="member-surname text-base-sm font-normal">{publisher?.displayName}</div>
        );
      },
    },
    {
      field: 'id',
      headerName: 'ID',
      flex: 1,
      minWidth: 100,
      renderCell: (params) => {
        const publisher = params.row;
        const publisherId = parseInt(publisher?.id, 10);
        const isAdformRange = publisherId >= 10000000;

        return (
          <div className="member-surname text-base-sm font-normal">
            {isAdformRange ? 'N/A' : publisher?.id}
          </div>
        );
      },
    },
    {
      field: 'creationDate',
      headerName: 'Date Created',
      flex: 1,
      minWidth: 100,
      filterable: true,
      renderCell: (params) => {
        const publisher = params.row;

        return (
          <div className="member-surname text-base-sm font-normal">{publisher?.creationDate}</div>
        );
      },
    },
    {
      field: 'dealsString',
      headerName: 'Deals',
      flex: 1,
      minWidth: 150,
      filterable: true,
      renderCell: (params) => {
        const publisher = params.row;
        const dealIds = publisher?.dealIds || [];
        const dealNames = dealIds
          .map((id: string) => adformDeals?.find((deal) => deal.dealId === id)?.name)
          .filter(Boolean);

        if (dealNames.length === 0) return <span className="text-primary-gray-400">-</span>;

        const displayText = dealNames.join(', ');
        const tooltipContent = dealNames.map((name: string, index: number) => (
          <div key={index}>{name}</div>
        ));

        return (
          <Tooltip title={<div className="flex flex-col gap-1">{tooltipContent}</div>}>
            <div className="text-base-sm font-normal truncate w-full">{displayText}</div>
          </Tooltip>
        );
      },
    },
    {
      field: 'sitesString',
      headerName: 'Sites',
      flex: 1,
      minWidth: 150,
      filterable: true,
      renderCell: (params) => {
        const publisher = params.row;
        const siteDomains = publisher?.siteDomains || [];

        if (siteDomains.length === 0) return <span className="text-primary-gray-400">-</span>;

        const displayText = siteDomains.join(', ');
        const tooltipContent = siteDomains.map((domain: string, index: number) => (
          <div key={index}>{domain}</div>
        ));

        return (
          <Tooltip title={<div className="flex flex-col gap-1">{tooltipContent}</div>}>
            <div className="text-base-sm font-normal truncate w-full">{displayText}</div>
          </Tooltip>
        );
      },
    },
    {
      field: 'appsString',
      headerName: 'Apps',
      flex: 1,
      minWidth: 150,
      filterable: true,
      renderCell: (params) => {
        const publisher = params.row;
        const appIds = publisher?.appIds || [];

        if (appIds.length === 0) return <span className="text-primary-gray-400">-</span>;

        const displayText = appIds.join(', ');
        const tooltipContent = appIds.map((appId: string, index: number) => (
          <div key={index}>{appId}</div>
        ));

        return (
          <Tooltip title={<div className="flex flex-col gap-1">{tooltipContent}</div>}>
            <div className="text-base-sm font-normal truncate w-full">{displayText}</div>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      sortable: false,
      cellClassName: 'actions',
      flex: 1,
      align: 'right',
      getActions: ({ id, row }) =>
        canEdit
          ? [
              <GridActionsCellItem
                icon={<Edit />}
                key={id}
                label="Edit"
                onClick={handleEditClick(id)}
                color="inherit"
              />,
              <GridActionsCellItem
                key={id}
                icon={<Trash2 />}
                label="Delete"
                onClick={handleDeleteClick(id)}
                color="inherit"
                disabled={row.role === UserRoles.SUPER_ADMIN.value}
              />,
            ]
          : [],
    },
  ];

  return (
    <div className="w-screen h-full bg-white rounded-xl border border-primary-gray-100 flex-col justify-start items-start flex">
      <div className="p-8 flex-col justify-start items-start inline-flex w-full">
        <div className="flex-col w-full justify-start items-start gap-4 flex">
          <div className="text-secondary-black text-heading-xl font-semibold leading-relaxed">
            Publishers
          </div>
          <div className="flex justify-between items-center w-full">
            {canEdit ? (
              <div className="flex space-x-4">
                <Button variant="primary" size="md" onClick={openCreateModal}>
                  Create new publisher
                </Button>
                <Button variant="primary" size="md" onClick={openUploadFileModal}>
                  Upload file
                </Button>
              </div>
            ) : null}
          </div>
          <Spin spinning={isLoading || refreshFlag}>
            <div className="grid w-full">
              <Grid
                columns={columns}
                rows={enrichedPublishers as any[] | undefined}
                filterModel={filterModel}
                onFilterModelChange={(model) => setFilterModel(model)}
                disableColumnMenu={false}
                disableColumnFilter={false}
                disableColumnSelector={false}
              />
            </div>
          </Spin>
        </div>
      </div>
      <Modal open={isCreateModalOpen} footer={null} closable={false} onCancel={closeCreateModal}>
        <CreatePublisherModal key={createModalKey} onClose={closeCreateModal} />
      </Modal>
      <Modal open={isEditModalOpen} footer={null} closable={false} onCancel={closeEditModal}>
        <EditPublisherModal
          key={editModalKey}
          onClose={closeEditModal}
          selectedPublisher={selectedPublisher}
        />
      </Modal>
      <Modal open={isDeleteModalOpen} footer={null} closable={false} onCancel={closeDeleteModal}>
        <DeletePublisherModal onClose={closeDeleteModal} selectedPublisher={selectedPublisher} />
      </Modal>
      <Modal
        open={isUploadFileModalOpen}
        footer={null}
        closable={false}
        onCancel={closeUploadFileModal}
      >
        <UploadFileModal onClose={closeUploadFileModal} />
      </Modal>
    </div>
  );
};
